import { useEffect, useState, useCallback } from 'react';
import { useSnackbar } from 'notistack';
import { getUsers, deleteUser } from '../features/users/services/usersService';
import { UserBlock } from '../features/users/components/UserBlock';
import { FormattedMessage, useIntl } from 'react-intl';
import DeleteIcon from '@mui/icons-material/Delete';
// material
import { Stack, Button, Grid, TextField, InputAdornment } from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import Iconify from '../components/Iconify';
import BackDropLoader from '../components/BackDropLoader';
import { getOnlyDate, getRoleName } from '../utils';
import { useDisclosureWithOpts } from '../hooks/useDisclosure';
import UserDialog from '../features/users/components/UserDialog';
import Table from '../components/Table/Table';
import { useAuth } from '../contexts/authContext';
import { useTheme  } from '@mui/material/styles';
import { useMediaQuery } from '@mui/material';

export default function UsersPage() {

  const intl = useIntl();
  const [loading, setLoading] = useState(false);
  const [userData, setUserData] = useState({ isFetching: true, users: [] });
  const { enqueueSnackbar } = useSnackbar();
  const dialogDisclosure = useDisclosureWithOpts();
  const auth = useAuth();
  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down('sm'));
  const [searchQuery, setSearchQuery] = useState('');

  const fetchData = useCallback(async (showSuccesMsg = true, showErrorMsg = true) => {
    try {
      const users = await getUsers(auth.user.id);
      if (showSuccesMsg) enqueueSnackbar(<FormattedMessage id='users.get.msg.success' defaultMessage='Users successfully fetched' />, {
        variant: 'success',
      });
      const preparedUsers = users.map(item => ({ ...item, country: item.country ?? item.country, role: getRoleName(item.roleId), active: getOnlyDate(new Date(item.validTo)) >= getOnlyDate(new Date()) }));
      setUserData({ isFetching: false, users: preparedUsers });
    } catch (error) {
      if (showErrorMsg) enqueueSnackbar(<FormattedMessage id='users.get.msg.error' defaultMessage='Users fetch failed' />, {
        variant: 'error',
      });
      setUserData({ isFetching: false, users: [] });
    }
  }, [setUserData]);

  useEffect(() => {
    fetchData();
  }, []);

  // const handleDeleteAll = async (rows) => {
  //   const ids = rows.map(item => item.id);
  //   setLoading(true);
  //   try {
  //     await deleteUsers(ids);
  //     await fetchData(false);
  //     enqueueSnackbar(<FormattedMessage id='users.delete.msg.success' defaultMessage='Users successfully Deleted' />, {
  //       variant: 'success',
  //     });
  //   } catch (error) {
  //     enqueueSnackbar(<FormattedMessage id='users.delete.msg.error' defaultMessage='Users delete failed' />, {
  //       variant: 'error',
  //     });
  //     console.error(error);
  //   } finally {
  //     setLoading(false);
  //   }
  // };

  const handleDelete = async (row) => {
    setLoading(true);
    try {
      await deleteUser(row.id);
      await fetchData(false);
      enqueueSnackbar(<FormattedMessage id='users.deleteAll.msg.success' defaultMessage='Users successfully Deleted' />, {
        variant: 'success',
      });
    } catch (error) {
      enqueueSnackbar(<FormattedMessage id='users.deleteAll.msg.error' defaultMessage='Users delete failed' />, {
        variant: 'error',
      });
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const handleRowClick = (rowData) => {
    dialogDisclosure.open({ id: rowData.id });
  };

  const handleButtonCreateUser = () => {
    dialogDisclosure.open();
  };

  const handleDialogClose = (refetchData) => {
    if (refetchData) {
      fetchData(false);
    }
    dialogDisclosure.close();
  };

  // Function to handle the search
  const handleSearch = useCallback(
    (query) => {
      setSearchQuery(query);
    },
    [setSearchQuery]
  );

  // Filter the users based on the search query
  const filteredUsers = userData.users.filter(
    (user) =>
      user.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      user.surname.toLowerCase().includes(searchQuery.toLowerCase()) ||
      user.email.toLowerCase().includes(searchQuery.toLowerCase()) ||
      user.role.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const handleUserBlockClick = (event, user) => {
    // Check if the click target is a button
    if (event.target.tagName === 'BUTTON') {
      // Do nothing if it's a button (Remove User button clicked)
      return;
    }
  
    // Call handleRowClick with user data if the user block is clicked
    handleRowClick(event, user);
  };

  return (
    <>
      {isSmallScreen ? (
        //mobile screen part
        <div>
          <BackDropLoader open={loading} />
          <Stack direction="row" alignItems="center" justifyContent="center" mb={5}>
            <Button
              variant="contained"
              startIcon={<Iconify icon="eva:plus-fill" />}
              onClick={handleButtonCreateUser}
              style={{ width: '100%' }}
            >
              <FormattedMessage id='common.button.createUser' defaultMessage='Create user' />
            </Button>
          </Stack>
          <TextField
            fullWidth
            variant="outlined"
            placeholder={ intl.formatMessage({ id: 'user.form.label.searchUser', defaultMessage: 'Search users...' })}
            onChange={(e) => handleSearch(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              ),
            }}
            style={{ marginBottom: '10px' }}
          />
          <Grid container spacing={2}>
            {searchQuery
              ? filteredUsers.map((user) => (
                <UserBlock key={user.id} user={user} onRowClick={handleUserBlockClick} onDeleteClick={handleDelete} />
              ))
              : userData.users.map((user) => (
                <UserBlock key={user.id} user={user} onRowClick={handleUserBlockClick} onDeleteClick={handleDelete} />
              ))}
          </Grid>
          {
            dialogDisclosure.isOpen &&
        <UserDialog open={dialogDisclosure.isOpen} id={dialogDisclosure.opts.id} onClose={handleDialogClose} />
          }
        </div>
      ) : (
        //desktop part
        <div>
          <BackDropLoader open={loading} />
          <Stack direction="row" alignItems="center" justifyContent="flex-end" mb={5}>
            <Button
              variant="contained"
              startIcon={<Iconify icon="eva:plus-fill" />}
              onClick={handleButtonCreateUser}
            >
              <FormattedMessage id='common.button.createUser' defaultMessage='Create user' />
            </Button>
          </Stack>
          <Table
            columns={[
              { title: intl.formatMessage({ id: 'user.form.label.firstname', defaultMessage: 'First name' }), field: 'name' },
              { title: intl.formatMessage({ id: 'user.form.label.lastname', defaultMessage: 'Last name' }), field: 'surname' },
              { title: intl.formatMessage({ id: 'user.form.label.emailAddress', defaultMessage: 'Email address' }), field: 'email' },
              { title: intl.formatMessage({ id: 'user.form.label.role', defaultMessage: 'Role' }), field: 'role' },
              { title: intl.formatMessage({ id: 'user.form.label.active', defaultMessage: 'Active' }), field: 'active', type: 'boolean' }
            ]}
            data={userData.users}
            onRowClick={(event, rowData) => handleRowClick(rowData)}
            actions={[
              // {
              //   tooltip: 'Remove All Selected Users',
              //   icon: DeleteIcon,
              //   onClick: (evt, data) => handleDeleteAll(data)
              // },
              {
                tooltip: intl.formatMessage({ id: 'users.tooltip.removeUser', defaultMessage: 'Remove User' }),
                icon: DeleteIcon,
                onClick: (evt, data) => handleDelete(data),
                position: 'row'
              }
            ]}
            isLoading={userData.isFetching}
          />
          {
            dialogDisclosure.isOpen &&
        <UserDialog open={dialogDisclosure.isOpen} id={dialogDisclosure.opts.id} onClose={handleDialogClose} />
          }
        </div>
      )}
    </>
  );
}